import { useState } from 'react';
import { useShellNavigate } from '../common/ShellLink';
import api from '../../services/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { apiErrorMessage } from '../../services/errorMessage';
import type { TrendingKeyword } from '../../types';

/**
 * Start a subject from keywords you have already researched.
 *
 * The point is the PLAN, not the empty subject. Creating a Project is one
 * endpoint away and always was; what made it useless from here is that you then
 * faced a blank workspace holding fifty keywords in your head. This hands the
 * structure planner the terms with their demand numbers and lets it propose the
 * tree — which was going to be the next step anyway, minus the retyping.
 */
export default function StartSubjectModal({
  keywords,
  onClose,
}: {
  keywords: TrendingKeyword[];
  onClose: () => void;
}) {
  useEscapeKey(true, onClose);
  const shellNavigate = useShellNavigate();
  // Seeded from the highest-volume term, which is the closest thing to "what is
  // this pile about" we can offer without asking.
  const [name, setName] = useState(
    () =>
      [...keywords].sort(
        (a, b) => (b.avg_monthly_searches ?? 0) - (a.avg_monthly_searches ?? 0),
      )[0]?.keyword_value ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.startSubjectFromKeywords(
        trimmed,
        keywords.map(k => k.keyword_id),
      );
      // The planner drawer opens on arrival and is already thinking.
      shellNavigate(`/subjects/${res.project_id}?plan=1`);
    } catch (e) {
      setError(apiErrorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl dark:bg-gray-800"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Start a subject</h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {keywords.length} keyword{keywords.length === 1 ? '' : 's'} go in. The structure
          planner proposes a category and topic tree over them, and you review it before
          anything is created.
        </p>

        <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
          What is this subject about?
        </label>
        <input
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          placeholder="e.g. Home solar"
        />

        <div className="mt-3 max-h-32 overflow-y-auto rounded bg-gray-50 p-2 dark:bg-gray-900/40">
          {keywords.map(k => (
            <div key={k.keyword_id} className="text-xs text-gray-600 dark:text-gray-300">
              {k.keyword_value}
              {k.avg_monthly_searches != null && (
                <span className="text-gray-400 dark:text-gray-500">
                  {' '}· {k.avg_monthly_searches.toLocaleString()}/mo
                </span>
              )}
            </div>
          ))}
        </div>

        {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !name.trim()}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create and plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
